import React, { useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react';
import { Alert, Button } from 'react-bootstrap';
import { BsJournalBookmark, BsPencil } from 'react-icons/bs';
import { FaDatabase, FaFolder } from 'react-icons/fa';
import FetchContainer from 'Utils/FetchContainer';
import AppContext from 'Utils/AppContext';
import { getSourceNomenclature } from 'Utils/nomenclature';
import { getDataSourceNumericId, isFileDatasourceType } from 'DataSources/datasourcesApi';
import {
  createDataSourceInventory,
  getMaskingEntityAlgorithmMap,
  selectedFieldsToInventory,
  updateInventory,
} from 'Secure/api';
import { getSelectedFieldsCount } from 'Secure/Utils';
import {
  discoverTablesToMaskingSelectedFields,
  searchDiscoverIndexTables,
} from 'Secure/secureDiscoverFields';
import ProviderBadge from 'Secure/ProviderBadge';
import HealthcareBadge from 'Secure/HealthcareBadge';
import { InventoryNameModal } from 'Secure/InventorySelector';
import InventoryFieldsAccordion from '../MaskingSources/MaskDialog/InventoryFieldsAccordion';
import InventoryIssuesBanner from '../MaskingSources/InventoryIssuesBanner';
import { collectInventoryIssues } from '../MaskingSources/uniquenessWarning';
import {
  clearAllSelections,
  loadMergedInventory,
  mergeMaskingInventoryWithDiscover,
} from '../MaskingSources/inventoryMerge';
import { filterMaskableFiles, sourceSubtitle, sourceSubtitleTitle } from './managerUtils';

const clone = (o) => JSON.parse(JSON.stringify(o || {}));

/**
 * Right-click menu for an inventory label (the detail-head label): Rename… (name popup),
 * Select provider ▸ (submenu of enabled masking providers, current one checked), Delete.
 * Rendered inside .im-shell so it picks up the manager's CSS variables.
 * `menu` is { x, y, inventory } or null.
 */
export function InventoryContextMenu({ menu, providers, currentCheck, onRename, onSelectProvider, onDelete, onClose }) {
  const [showProviders, setShowProviders] = useState(false);

  useEffect(() => {
    setShowProviders(false);
    if (!menu) return undefined;
    const close = () => onClose();
    const onKey = (e) => {
      if (e.key === 'Escape') onClose();
    };
    document.addEventListener('click', close);
    document.addEventListener('contextmenu', close);
    document.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('click', close);
      document.removeEventListener('contextmenu', close);
      document.removeEventListener('keydown', onKey);
    };
  }, [menu, onClose]);

  if (!menu || !menu.inventory) return null;
  const inv = menu.inventory;
  const current = inv.preferredMaskingProviderId || '';
  // Keep the menu on screen when opened near the viewport edge.
  const top = Math.min(menu.y, window.innerHeight - 150);
  const left = Math.min(menu.x, window.innerWidth - 210);

  return (
    <div
      className='im-ctx'
      style={{ top, left }}
      data-testid='im-inventory-context-menu'
      onClick={(e) => e.stopPropagation()}
      onContextMenu={(e) => e.preventDefault()}
    >
      <button
        type='button'
        className='im-ctx-item'
        onClick={() => {
          onClose();
          onRename(inv);
        }}
      >
        Rename…
      </button>
      <div
        className='im-ctx-item im-ctx-sub-parent'
        onMouseEnter={() => setShowProviders(true)}
        onMouseLeave={() => setShowProviders(false)}
        onClick={() => setShowProviders((v) => !v)}
        role='button'
        tabIndex={0}
      >
        <span>Select provider</span>
        <span className='im-ctx-caret'>▸</span>
        {showProviders && (
          <div className='im-ctx-sub'>
            <button
              type='button'
              className='im-ctx-item'
              onClick={() => {
                onClose();
                onSelectProvider(inv, '');
              }}
            >
              <span>Use default</span>
              {!current && currentCheck}
            </button>
            {(providers || []).map((p) => (
              <button
                type='button'
                key={p.serviceId}
                className='im-ctx-item'
                onClick={() => {
                  onClose();
                  onSelectProvider(inv, p.serviceId);
                }}
              >
                {/* Native C2 provider (no vendor) shows the friendly "C2 Masking" label,
                    matching ProviderBadge / ProviderSelectCell; 3rd-party shows its own name. */}
                <span>{p.vendor ? (p.vendorDisplayName || p.name) : 'C2 Masking'}</span>
                {current === p.serviceId && currentCheck}
              </button>
            ))}
          </div>
        )}
      </div>
      <button
        type='button'
        className='im-ctx-item im-ctx-danger'
        onClick={() => {
          onClose();
          onDelete(inv);
        }}
      >
        Delete
      </button>
    </div>
  );
}

/**
 * Detail pane for the selected (source, inventory) pair: buffered editor + Save/Cancel.
 * Inventory selection lives in the head's `inventorySelector` slot (the host passes an
 * InventorySelector dropdown); rename and provider selection live in the inventory label's
 * right-click menu. The head keeps the provider badge (top right) and the inventory label;
 * Delete moved to the page-header Actions dropdown. `reloadKey` bumps force a reload after
 * an out-of-band change (e.g. provider picked via the context menu). `exportDraftRef`
 * mirrors the live draft so the page-header export actions capture unsaved edits.
 */
function SourceDetail({
  source,
  inventories,
  selectedInventoryId,
  setSelectedInventoryId,
  refreshInventories,
  onInventoryContextMenu,
  algorithms,
  providers,
  defaultEntityAlgorithmMap,
  reloadKey,
  exportDraftRef,
  inventorySelector,
}) {
  const { setNotification } = useContext(AppContext);
  const dataSourceId = getDataSourceNumericId(source);
  const nomenclature = getSourceNomenclature(source?.type, source?.datasourceType);

  const [nameModalOpen, setNameModalOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [draft, setDraft] = useState({});
  const [baseline, setBaseline] = useState({}); // last-saved snapshot for Cancel + dirty check
  const [noDiscover, setNoDiscover] = useState(false);
  const [saving, setSaving] = useState(false);
  // Per-table {sniffedType, delimiter} from the discovery overview — drives file
  // maskability and rides on saved inventory tables for masking format routing.
  const fileTableMetaRef = useRef({});
  // Table-index (PeopleSoft-80K) runs: { runId, totalTables, loadedCount } when the load
  // was paged from the index; null for classic runs. Enables the accordion's server-search
  // fallback when capped. The entity map ref mirrors what the load effect resolved so
  // server-merged tables get the same recommendations.
  const [indexInfo, setIndexInfo] = useState(null);
  const entityMapRef = useRef({});

  // Keep the page-header export actions in sync with the on-screen draft.
  useEffect(() => {
    if (exportDraftRef) exportDraftRef.current = draft;
  }, [draft, exportDraftRef]);

  // Latest inventory list without re-running the merge effect when the list identity changes
  // (a rename refresh must not wipe in-progress draft edits).
  const inventoriesRef = useRef(inventories);
  useEffect(() => {
    inventoriesRef.current = inventories;
  }, [inventories]);

  const selectedInventory =
    selectedInventoryId != null
      ? (inventories || []).find((inv) => inv && inv.id === selectedInventoryId) || null
      : null;
  // Badge + recommendations reflect THIS inventory's provider (change it via right-click on the
  // inventory label), not a global default.
  const providerId = selectedInventory?.preferredMaskingProviderId || null;

  // Reload whenever the selected source or inventory changes. Resolve the effective provider
  // (inventory preference → registry default → first) and load THAT provider's entity→algorithm
  // map so the recommendations match, mirroring the masking inventory page.
  useEffect(() => {
    let cancelled = false;
    const run = async () => {
      // undefined = the host is still resolving which inventory to show.
      if (dataSourceId == null || selectedInventoryId === undefined) return;
      setLoading(true);
      setError(null);
      try {
        const inventory =
          selectedInventoryId != null
            ? (inventoriesRef.current || []).find((inv) => inv && inv.id === selectedInventoryId) || null
            : null;
        const preferredId = inventory?.preferredMaskingProviderId;
        const preferred = preferredId && (providers || []).find((p) => p.serviceId === preferredId);
        const defaultProv = (providers || []).find((p) => p.isDefault);
        const effectiveProvider =
          (preferred && preferred.serviceId)
          || (defaultProv && defaultProv.serviceId)
          || (providers && providers[0] ? providers[0].serviceId : null);
        // Pass dataSourceId so a healthcare-tagged source gets the safe-harbor-overlaid map
        // (mirrors InventoryPage; without it, healthcare recommendations never seed here).
        const map = await getMaskingEntityAlgorithmMap(effectiveProvider || undefined, dataSourceId)
          .catch(() => defaultEntityAlgorithmMap);

        const resolvedEntityMap = map && typeof map === 'object' ? map : {};
        entityMapRef.current = resolvedEntityMap;
        setIndexInfo(null);
        const { selectedFields, noDiscoverFields, fileTableMeta } = await loadMergedInventory(
          dataSourceId,
          resolvedEntityMap,
          undefined,
          inventory,
          {
            onWarning: (notification) => setNotification(notification),
            onIndexInfo: (info) => {
              if (!cancelled) setIndexInfo(info);
            },
          }
        );
        if (cancelled) return;
        fileTableMetaRef.current = fileTableMeta || {};
        const maskableFields = filterMaskableFiles(selectedFields, source, fileTableMetaRef.current);
        setDraft(maskableFields);
        setBaseline(clone(maskableFields));
        setNoDiscover(noDiscoverFields);
      } catch (e) {
        if (!cancelled) setError(e?.message || 'Unable to load inventory');
      } finally {
        if (!cancelled) setLoading(false);
      }
    };
    run();
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dataSourceId, selectedInventoryId, providers, defaultEntityAlgorithmMap, reloadKey]);

  const dirty = useMemo(() => JSON.stringify(draft) !== JSON.stringify(baseline), [draft, baseline]);

  // Confirmed uniqueness conflicts for the red banner — same descriptors the accordion
  // renders, so the banner and the per-row icons never disagree. Relational sources only.
  // `algorithms` is a dependency because the catalog load registers which algorithms the worker
  // declares uniqueness-safe (registerAlgorithmCatalog); it can land after the draft does.
  const inventoryIssues = useMemo(
    () => (isFileDatasourceType(source?.datasourceType) ? [] : collectInventoryIssues(draft)),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [draft, source, algorithms]
  );

  // Server-search fallback for capped index-mode loads: fetch matching tables from the
  // WHOLE run and add any missing ones. Added to BOTH draft and baseline — surfacing a
  // table is a view augmentation, not an edit, so it must not flip the dirty flag.
  const handleServerSearch = useCallback(async (q) => {
    if (!indexInfo) return;
    const { tables } = await searchDiscoverIndexTables(indexInfo.runId, q);
    if (!tables.length) return;
    const shaped = mergeMaskingInventoryWithDiscover(
      {},
      discoverTablesToMaskingSelectedFields(tables),
      entityMapRef.current
    );
    const addMissing = (prev) => {
      let added = false;
      const next = { ...prev };
      Object.keys(shaped).forEach((tk) => {
        if (!next[tk]) {
          next[tk] = shaped[tk];
          added = true;
        }
      });
      return added ? next : prev;
    };
    setDraft(addMissing);
    setBaseline(addMissing);
  }, [indexInfo]);

  const serverSearch = useMemo(
    () =>
      indexInfo && indexInfo.totalTables > indexInfo.loadedCount
        ? { totalTables: indexInfo.totalTables, onSearch: handleServerSearch }
        : null,
    [indexInfo, handleServerSearch]
  );
  const selectedCount = getSelectedFieldsCount(draft);
  // A fresh draft (no saved inventory yet) is savable as-is: the discover-seeded state IS the
  // content to persist, so Save can't be gated on `dirty` (baseline === draft on first load —
  // that gate made it impossible to save after a discovery, leaving nothing for Job Groups to
  // select). Saved inventories keep the dirty gate: nothing changed, nothing to save.
  const isNewDraft = selectedInventoryId == null;
  const canSave = isNewDraft ? Object.keys(draft).length > 0 : dirty;

  // True when at least one field carries a recommended algorithm OR a set algorithm — gates the
  // "Accept all recommendations" button so it's only offered when accepting could change
  // something. A set algorithm with no recommendation counts: accepting its (empty)
  // recommendation clears it.
  const hasRecommendations = useMemo(
    () =>
      Object.values(draft || {}).some((table) =>
        Object.values(table || {}).some((f) => f && (f.recommendedAlgorithm || f.algorithm))
      ),
    [draft]
  );

  const onCancel = () => setDraft(clone(baseline));

  // Accept every field's recommendation as the masking decision: for each field that has a
  // recommended algorithm, set its algorithm (and params) to that recommendation AND check it
  // for masking. `checked` and `algorithm` are independent in this model (setting an algorithm
  // alone does not select the field), so we set both. A field with NO recommendation is accepted
  // too: its recommendation is "none", so a previously set algorithm is cleared and the field
  // unchecked — a stale selection from an older discover must not survive Accept all. Staged
  // only; persisted on Save.
  const onAcceptAllRecommendations = () =>
    setDraft((prev) => {
      const out = {};
      for (const t of Object.keys(prev || {})) {
        out[t] = {};
        for (const f of Object.keys(prev[t] || {})) {
          const fd = prev[t][f];
          const rec = fd && fd.recommendedAlgorithm ? fd.recommendedAlgorithm : '';
          out[t][f] = rec
            ? { ...fd, algorithm: rec, params: fd.recommendedParams || {}, checked: true }
            : { ...fd, algorithm: '', params: {}, checked: false };
        }
      }
      return out;
    });

  // Uncheck every field and clear its algorithm — masks nothing. Staged only; persisted on Save.
  const onClearAll = () => setDraft((prev) => clearAllSelections(prev));

  const doSave = async (name) => {
    if (dataSourceId == null) return;
    setSaving(true);
    try {
      // fileSource: keep file-name table keys verbatim (no dot-split) — see Secure/api.js.
      // tableMeta rides sniffedType/delimiter onto each saved table for masking routing.
      const content = selectedFieldsToInventory(draft, source.name || '', 'ui-user', {
        fileSource: isFileDatasourceType(source?.datasourceType),
        tableMeta: fileTableMetaRef.current,
      });
      if (selectedInventoryId != null) {
        // Content-only partial PUT: the inventory's provider preference is managed via the
        // right-click menu and must survive a field-selection save untouched.
        await updateInventory(selectedInventoryId, { content });
        await refreshInventories();
      } else {
        const created = await createDataSourceInventory(dataSourceId, { name, content });
        await refreshInventories();
        if (created && created.id != null) setSelectedInventoryId(created.id);
      }
      setBaseline(clone(draft));
      setNotification({ header: 'Inventory saved', body: `Saved masking inventory for ${source.name}` });
    } catch (e) {
      setNotification({ header: 'Save failed', body: e?.message || String(e) });
    } finally {
      setSaving(false);
    }
  };

  const onSave = () => {
    if (selectedInventoryId == null) {
      setNameModalOpen(true);
      return;
    }
    doSave();
  };

  const inventoryLabel = selectedInventory
    ? selectedInventory.name || `Inventory ${selectedInventory.id}`
    : 'New inventory (unsaved)';

  return (
    <div className='im-detail'>
      {/* Static head: two-row title (name + engine / # selected), like the inventory pages. */}
      <div className='im-detail-head d-flex align-items-start justify-content-between'>
        <div>
          <div className='im-detail-title'>{source.name}</div>
          <div className='im-detail-sub'>
            {isFileDatasourceType(source?.datasourceType) ? (
              <FaFolder className='mr-1' />
            ) : (
              <FaDatabase className='mr-1' />
            )}
            {/* Hover reveals the full path for file sources. */}
            <span title={sourceSubtitleTitle(source)}>{sourceSubtitle(source)}</span>
            {' · '}{selectedCount} selected to mask
          </div>
          <div className='im-detail-sub mt-1 d-flex align-items-center' style={{ gap: 8 }}>
            <BsJournalBookmark />
            <span
              data-testid='im-inventory-label'
              onContextMenu={
                selectedInventory ? (e) => onInventoryContextMenu(e, selectedInventory) : undefined
              }
              title={selectedInventory ? 'Right-click to rename or change provider' : undefined}
            >
              {inventoryLabel}
            </span>
          </div>
        </div>
        <div className='d-flex flex-column align-items-end' style={{ gap: '0.4rem' }}>
          {inventorySelector}
          <HealthcareBadge
            healthcareTag={source?.healthcareTag === true}
            healthcareTagSource={source?.healthcareTagSource || ''}
          />
          <ProviderBadge providers={providers} serviceId={providerId} defaultLabel='C2 Masking' />
        </div>
      </div>

      {/* Scroll body — the accordion's filters stay sticky at its top; this region scrolls between
          the static head and foot. FetchContainer wraps only the inner content so head/body/foot
          remain direct flex children of .im-detail (the bounded flex column). */}
      <div className='im-detail-body'>
        <FetchContainer error={error} fetchingData={loading} minHeight='300px'>
          {noDiscover ? (
            <Alert variant='warning'>
              No fields loaded from discovery for this data source. Run a completed discover job first;
              masking inventory is built from discovery results only.
            </Alert>
          ) : !loading &&
            isFileDatasourceType(source?.datasourceType) &&
            Object.keys(draft).length === 0 ? (
            <Alert variant='info'>
              None of the files in this inventory have a format supported by masking.
            </Alert>
          ) : (
            <>
              <InventoryIssuesBanner issues={inventoryIssues} />
              <InventoryFieldsAccordion
                selectedFields={draft}
                setSelectedFields={setDraft}
                algorithms={algorithms}
                serverSearch={serverSearch}
                nomenclature={nomenclature}
              />
            </>
          )}
        </FetchContainer>
      </div>

      {/* Static foot. */}
      <div className='im-detail-foot'>
        <span className='d-flex align-items-center' style={{ gap: 8 }}>
          {hasRecommendations && (
            <Button
              size='sm'
              variant='outline-secondary'
              disabled={saving || loading || noDiscover}
              onClick={onAcceptAllRecommendations}
              data-testid='im-accept-all-recommendations'
            >
              Accept all
            </Button>
          )}
          <Button
            size='sm'
            variant='outline-secondary'
            disabled={saving || loading || noDiscover || selectedCount === 0}
            onClick={onClearAll}
            data-testid='im-clear-all'
          >
            Clear all
          </Button>
          <span className={dirty || isNewDraft ? 'im-dirty' : 'im-clean'}>
            {dirty ? (
              <>
                <BsPencil className='mr-1' />
                Unsaved changes
              </>
            ) : isNewDraft ? (
              <>
                <BsPencil className='mr-1' />
                Not saved yet
              </>
            ) : (
              'All changes saved'
            )}
          </span>
        </span>
        <span className='d-flex' style={{ gap: 8 }}>
          <Button size='sm' variant='outline-secondary' disabled={!dirty || saving} onClick={onCancel}>
            Cancel
          </Button>
          <Button
            size='sm'
            variant='primary'
            disabled={!canSave || saving || loading || noDiscover}
            onClick={onSave}
            data-testid='im-save'
          >
            Save
          </Button>
        </span>
      </div>
      <InventoryNameModal
        show={nameModalOpen}
        title='Name this inventory'
        initialName={source.name ? `${source.name} masking` : ''}
        submitLabel='Save'
        onSubmit={(name) => {
          setNameModalOpen(false);
          doSave(name);
        }}
        onCancel={() => setNameModalOpen(false)}
      />
    </div>
  );
}

export default SourceDetail;
